import update from 'immutability-helper'
import { isEqual } from 'lodash'
import { EXPAND_ALL } from '../../../dux/constants/permissions'
import { buildPath } from '../../../dux/modules/utils'

const validFolderTypes = ['folder', 'root', 'resources', 'webfolder']

// The only system group whose folder permissions an admin may change. Every
// other system group is shown read-only: either its access comes from
// membership (see getImplicitRoles) or its roles are the tenant defaults.
export const EDITABLE_SYSTEM_GROUP = 'SYS.allusers'

export const isSystemGroup = (entity) => String(entity).startsWith('SYS.')

export const isLockedEntity = (entity) =>
  isSystemGroup(entity) && entity !== EDITABLE_SYSTEM_GROUP

/*
 * True when the endpoint is the one assets are published to. Mirrors
 * isPublishedEndpoint in bam-api-services: older endpoints have no `type`.
 */
export const isPublishedEndpoint = (endpoint) =>
  !!endpoint &&
  (endpoint.id === 'MST' || String(endpoint.type || '').toLowerCase() === 'mst')

/*
 * Roles a system group holds on a folder by membership alone, whatever the
 * folder's contentRoles say (hasSystemViewAccess in bam-api-services, and
 * validatePrivilege here for the admin groups):
 * - System: Admins / Content Admins can do everything everywhere
 * - System: Viewers sees every folder
 * - System: View Published sees every folder in the Published endpoint
 */
export const getImplicitRoles = (entity, roles, inPublished) => {
  switch (entity) {
    case 'SYS.admins':
    case 'SYS.contentadmins':
      return roles.filter((role) => role.contentRole).map((role) => role.id)
    case 'SYS.viewers':
      return ['viewer', 'traverser']
    case 'SYS.mobileusers':
      return inPublished ? ['viewer', 'traverser'] : []
    default:
      return []
  }
}

/**
 * Escapes special regex characters in a string to make it safe for use in RegExp constructor
 * @param {string} str - The string to escape
 * @returns {string} The escaped string safe for regex use
 */
export const escapeRegex = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const getEditableRootFolders = (rootFolders, allFolders) =>
  rootFolders.filter(
    (folder) => validFolderTypes.indexOf(allFolders[folder].type) > -1,
  )

export const getDefaultExpandedNodes = (endpoints, allFolders) => {
  const defaultNodes = endpoints.reduce((result, node) => {
    result[node.id] = {
      expandedNodes: getEditableRootFolders(node.rootFolders, allFolders),
      expandAllToggle: EXPAND_ALL,
      filterOption: 'all',
    }
    return result
  }, {})
  return defaultNodes
}

export const getAllParentNodes = (endpointId, endpoints, allFolders) => {
  let nodes = []
  const ep = endpoints.filter((endpoint) => endpoint.id === endpointId)[0]
  const roots = getEditableRootFolders(ep.rootFolders, allFolders)
  // Only include folders that have children, and are subfolders of the given root
  Object.keys(allFolders).forEach((folder) => {
    if (
      allFolders[folder].childFolders.length > 0 &&
      buildPath(folder, allFolders).some(
        (element) => roots.indexOf(element) > -1,
      )
    )
      nodes.push(folder)
  })
  return nodes
}

export const getChangedContentRoles = (contentRoles = {}, allFolders) => {
  let changedRoles = {}
  Object.keys(allFolders).forEach((folder) => {
    if (!isEqual(allFolders[folder].contentRoles, contentRoles[folder]))
      changedRoles[folder] = contentRoles[folder]
  })
  return changedRoles
}

export const hasTraverserOnly = (contentRoles, folder, entity) => {
  const roles = contentRoles[folder][entity].roleIds
  return roles.length === 1 && roles[0] === 'traverser'
}

export const removeEntityFromFolder = (state, folder, entity) => {
  return update(state, {
    contentRoles: {
      [folder]: {
        $unset: [entity],
      },
    },
  })
}

export const removeRoleFromEntity = (state, folder, entity, roleToRemove) => {
  return update(state, {
    contentRoles: {
      [folder]: {
        [entity]: {
          roleIds: {
            $apply: (x) => x.filter((role) => role !== roleToRemove),
          },
        },
      },
    },
  })
}

export const addEntityToFolder = (
  state,
  folder,
  entity,
  entityType,
  rolesToAdd,
) => {
  return update(state, {
    contentRoles: {
      [folder]: {
        [entity]: {
          $set: {
            entityType: entityType,
            entityId: entity,
            roleIds: rolesToAdd,
          },
        },
      },
    },
  })
}

export const addRoleToEntity = (state, folder, entity, roleArray) => {
  return update(state, {
    contentRoles: {
      [folder]: {
        [entity]: {
          roleIds: {
            $push: roleArray,
          },
        },
      },
    },
  })
}
